/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 * Written by Hendrik, December 28, 2025.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';

export default function BrandingSettings() {
    const [currentLogo, setCurrentLogo] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Load current branding settings from API
        loadBrandingSettings();
    }, []);

    const loadBrandingSettings = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/get_branding.php`,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                    }
                }
            );

            if (response.data.success && response.data.settings.custom_logo_path) {
                setCurrentLogo(response.data.settings.custom_logo_path);
                localStorage.setItem('custom_logo_path', response.data.settings.custom_logo_path);
            }
        } catch (err) {
        } finally {
            setLoading(false);
        }
    };

    const handleLogoUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!allowedTypes.includes(file.type)) {
            setError('Only JPG and PNG images are allowed');
            return;
        }

        // Validate file size (2MB)
        if (file.size > 2 * 1024 * 1024) {
            setError('File size must be under 2MB');
            return;
        }

        setError('');
        setUploading(true);

        try {
            const formData = new FormData();
            formData.append('logo', file);

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/update_branding.php`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                    }
                }
            );

            if (response.data.success) {
                setCurrentLogo(response.data.logo_path);
                localStorage.setItem('custom_logo_path', response.data.logo_path);
                alert('Logo uploaded successfully!');
                // Reload settings to ensure consistency
                loadBrandingSettings();
            } else {
                setError(response.data.error || 'Upload failed');
            }
        } catch (err) {
            setError('Upload failed. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="setting-group" style={{ marginBottom: '30px', padding: '20px', border: '1px solid #eee', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '15px', color: '#333' }}>School Branding</h3>
            <p style={{ marginBottom: '15px', color: '#666', fontSize: '0.9em' }}>
                Upload your school or organization logo to customize the app branding.
            </p>

            {error && (
                <div style={{ color: '#dc3545', marginBottom: '15px', padding: '10px', background: '#f8d7da', borderRadius: '4px' }}>
                    {error}
                </div>
            )}

            <div style={{ marginBottom: '20px' }}>
                <h4 style={{ marginBottom: '10px', fontSize: '0.9em', color: '#495057' }}>Current Logo:</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    {currentLogo ? (
                        <img
                            src={`${process.env.REACT_APP_API_URL}${currentLogo}`}
                            alt="Current Logo"
                            style={{ maxHeight: '60px', maxWidth: '200px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                    ) : (
                        <div style={{
                            width: '200px',
                            height: '60px',
                            border: '2px dashed #ccc',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#999',
                            fontSize: '0.9em'
                        }}>
                            No logo uploaded
                        </div>
                    )}
                </div>
            </div>

            <div>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Upload New Logo:
                </label>
                <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={handleLogoUpload}
                    disabled={uploading}
                    style={{
                        padding: '8px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        width: '100%',
                        maxWidth: '300px'
                    }}
                />
                <p style={{ marginTop: '5px', fontSize: '0.8em', color: '#666' }}>
                    JPG or PNG, max 2MB
                </p>
                {uploading && (
                    <p style={{ marginTop: '10px', color: '#007bff' }}>
                        Uploading... Please wait.
                    </p>
                )}
            </div>
        </div>
    );
}